/* How the app feels on the farm's phones, sent to POST /client-events
   (backend server/telemetry.py; summary at GET /client-events/summary).

   kinds: page (tap -> the page's data has arrived; the first one is the cold open),
   slow_api (a call over SLOW_API_MS), api_error (no answer / timeout / 5xx),
   js_error, crash (PageErrorBoundary), chunk_error, socket_drop (how long live data
   was gone), stale_build (an open tab found a newer deploy), jank (main thread
   blocked >= LONG_TASK_MS).

   Batched and sent with sendBeacon, so a report never competes with the page's own
   requests and still leaves when the tab is closed. Nothing here may throw. */

const BASE = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : '');
const ENDPOINT = `${BASE}/client-events`;

const SLOW_API_MS = 2000;
const LONG_TASK_MS = 200;
const FLUSH_EVERY_MS = 20000;
const QUEUE_MAX = 200;
const SAME_ERROR_MAX = 5;         // one broken render can throw on every frame
const PAGE_QUIET_MS = 500;        // no request for this long = the page has its data
const PAGE_GIVE_UP_MS = 30000;

const MAIN_RE = /\/static\/js\/main\.([0-9a-f]+)\.js/;
/* The main bundle this tab is running; null under `npm start`. */
export const runningBundle = (() => {
  const m = [...document.scripts].map(s => s.src.match(MAIN_RE)).find(Boolean);
  return m ? m[0] : null;
})();
const BUILD = runningBundle ? runningBundle.match(MAIN_RE)[1] : 'dev';
export const BUNDLE_RE = MAIN_RE;

let queue = [];
let started = false;
const errorCounts = {};

export function report(kind, fields = {}) {
  try {
    if (!started) return;
    if (queue.length >= QUEUE_MAX) queue.shift();
    queue.push({
      kind,
      t: Math.round(Date.now() / 1000),
      path: window.location.pathname,
      build: BUILD,
      ...fields,
      ms: fields.ms == null ? undefined : Math.round(fields.ms),
    });
  } catch { /* never let reporting break the app */ }
}

export function flush() {
  try {
    if (!queue.length) return;
    const batch = queue.splice(0, 50);
    const body = JSON.stringify({ events: batch });
    const sent = navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }));
    if (!sent) {
      fetch(ENDPOINT, { method: 'POST', body, keepalive: true, credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' } }).catch(() => {});
    }
    if (queue.length) flush();
  } catch { /* ignore */ }
}

export function reportError(kind, message, details) {
  const key = `${kind}:${message}`;
  errorCounts[key] = (errorCounts[key] || 0) + 1;
  if (errorCounts[key] > SAME_ERROR_MAX) return;
  report(kind, { name: String(message || 'unknown').slice(0, 300), details });
}

/* ---- network activity: what "the page has its data" is measured against ---- */

let inflight = 0;
let lastActivity = 0;
const touch = () => { lastActivity = performance.now(); };

/* Count a promise (a lazy page chunk) as page activity. */
export function trackPending(promise) {
  inflight += 1; touch();
  const done = () => { inflight -= 1; touch(); };
  promise.then(done, done);
  return promise;
}

/* Calls `fn` once no request has been in flight for PAGE_QUIET_MS (the page has its
   data), or after `maxMs` regardless. Returns a cancel function. */
export function whenQuiet(fn, maxMs) {
  const t0 = performance.now();
  let id = null;
  const poll = () => {
    const now = performance.now();
    if ((inflight === 0 && now - lastActivity >= PAGE_QUIET_MS) || now - t0 > maxMs) fn();
    else id = setTimeout(poll, 200);
  };
  id = setTimeout(poll, 200);
  return () => clearTimeout(id);
}

const shortUrl = (config) => `${(config.method || 'get').toUpperCase()} ${String(config.url || '').split('?')[0]}`;

export function instrumentAxios(instance) {
  instance.interceptors.request.use((config) => {
    config.metadata = { t0: performance.now() };
    inflight += 1; touch();
    return config;
  });
  const settle = (config) => {
    if (config?.metadata && !config.metadata.done) {
      config.metadata.done = true;
      inflight -= 1; touch();
    }
    return config?.metadata ? performance.now() - config.metadata.t0 : null;
  };
  instance.interceptors.response.use(
    (response) => {
      const ms = settle(response.config);
      // Uploads are slow because photos are big, not because anything is wrong.
      if (ms > SLOW_API_MS && !(response.config.data instanceof FormData)) {
        report('slow_api', { name: shortUrl(response.config), ms, status: response.status });
      }
      return response;
    },
    (error) => {
      const config = error.config || {};
      const ms = settle(config);
      const status = error.response?.status || 0;
      const cancelled = error.code === 'ERR_CANCELED';
      if (!cancelled && (status === 0 || status >= 500)) {
        report('api_error', { name: shortUrl(config), ms, status,
          details: { code: error.code, online: navigator.onLine, hidden: document.hidden } });
      }
      return Promise.reject(error);
    },
  );
}

/* ---- page timing: from the tap (or the cold open) until the page's data is in ---- */

let pageSeq = 0;
export function startPage(isCold) {
  const seq = ++pageSeq;
  const t0 = isCold ? 0 : performance.now();
  const startedActivity = isCold ? -1 : lastActivity;   // the cold open counts its sign-in check too
  const poll = () => {
    if (seq !== pageSeq) return;                       // left before it finished
    const now = performance.now();
    const quiet = inflight === 0 && now - lastActivity >= PAGE_QUIET_MS;
    if ((quiet && lastActivity > startedActivity) || now - t0 > PAGE_GIVE_UP_MS) {
      const ms = (inflight === 0 ? lastActivity : now) - t0;
      const details = { inflight, hidden: document.hidden };
      if (isCold) {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav) Object.assign(details, { cold: true, ttfb: Math.round(nav.responseStart),
          dom: Math.round(nav.domContentLoadedEventEnd), type: nav.type });
      }
      report('page', { name: window.location.pathname, ms, details });
      return;
    }
    if (quiet && now - t0 > 3000) return;              // a page with nothing to fetch
    setTimeout(poll, 100);
  };
  setTimeout(poll, 100);
}

/* ---- global listeners, once, after sign-in ---- */

export function startTelemetry() {
  if (started) return;
  started = true;
  window.addEventListener('error', (e) => {
    if (/ResizeObserver loop/.test(e.message || '')) return;   // harmless browser noise
    reportError('js_error', e.message, { src: `${e.filename || ''}:${e.lineno || ''}:${e.colno || ''}` });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    if (r?.isAxiosError) return;                                  // already api_error
    reportError('js_error', r?.message || String(r), { unhandled: true, stack: String(r?.stack || '').slice(0, 500) });
  });
  try {
    let count = 0; let worst = 0;
    new PerformanceObserver((list) => {
      list.getEntries().forEach((e) => {
        if (e.duration >= LONG_TASK_MS) { count += 1; worst = Math.max(worst, e.duration); }
      });
    }).observe({ type: 'longtask', buffered: true });
    setInterval(() => {
      if (count) { report('jank', { ms: worst, details: { count } }); count = 0; worst = 0; }
    }, FLUSH_EVERY_MS);
  } catch { /* longtask unsupported (Safari) */ }
  setInterval(flush, FLUSH_EVERY_MS);
  document.addEventListener('visibilitychange', () => { if (document.hidden) flush(); });
  window.addEventListener('pagehide', flush);
}
